
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { PaletteMode } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import AppAppBar from "../../components/layout/AppAppBar";
import Hero from "../../components/landing/Hero";
import FAQ from "../../components/landing/FAQ";
import Footer from "../../components/layout/Footer";
import getLPTheme from "../../getLPTheme";
// import Testimonials from "../../components/landing/Testimonials";
import HowKoinAppWorks from "../../components/landing/HowKoinAppWorks";
import PayBanner from "../../components/common/PayBanner";
import coinFlipGif from "../../assets/coin_flip.gif";



export default function LandingPage() {
  const [mode, setMode] = React.useState<PaletteMode>("light");
  // const [showCustomTheme, setShowCustomTheme] = React.useState(true);
  const LPtheme = createTheme(getLPTheme(mode));
  // const [showFloater, setShowFloater] = React.useState(false);
  // const [amount, setAmount] = React.useState(0);
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  // const [isMobile, setIsMobile] = React.useState(true); // Default to true to avoid flash

  // ✅ ADD STATE FOR SOURCE TRACKING
  // const [adSource, setAdSource] = React.useState<string | null>(null);

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleBannerStateChange = (isVisible: boolean) => {
    setIsBannerVisible(isVisible);
  };

  React.useEffect(() => {
    // Check if device is mobile (max width 768px for phones only)
    /* const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 &&
        /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile); */

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.location.href = "/ios";
      return;
    }


    // const match = currentUrl.match(/\/(\d+)\/?$/);
    // const number = match ? parseInt(match[1], 10) : 0; // Unused

    // ✅ CAPTURE SOURCE PARAMETER FROM URL
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source') || urlParams.get('utm_source');

    if (source) {
      sessionStorage.setItem('ad_source', source);
    }

    // ✅ CHECK FOR FB PATH OR SOURCE
    if (window.location.pathname === '/fb' || source === 'facebook') {
      sessionStorage.setItem('fb_user', 'true');
      sessionStorage.setItem('ad_source', 'facebook');
      localStorage.setItem('fb_user', 'true');
    } else if (window.location.pathname !== '/fb' && source !== 'facebook') {
      // If not on FB landing and no FB source in URL, we treat as potentially direct
      // Clear legacy localStorage to prevent "stuck" FB status for direct users
      localStorage.removeItem('fb_user');
      // Only clear session if we're at the root or a non-FB specific sub-page
      if (window.location.pathname === '/' || !window.location.pathname.startsWith('/fb')) {
        sessionStorage.removeItem('fb_user');
        if (sessionStorage.getItem('ad_source') === 'facebook') {
          sessionStorage.removeItem('ad_source');
        }
      }
    }

    // ✅ INSTALL RETURN LOGIC
    const checkInstallReturn = () => {
      const hasInitiatedInstall = localStorage.getItem('install_initiated');
      if (hasInitiatedInstall === 'true' && document.visibilityState === 'visible') {
        localStorage.removeItem('install_initiated');

        // Determine if FB user (Check session state)
        const isFbUser = sessionStorage.getItem('fb_user') === 'true' ||
          sessionStorage.getItem('ad_source') === 'facebook';

        if (isFbUser) {
          window.location.href = '/submit-task-fb';
        } else {
          window.location.href = '/submit-task';
        }
      }
    };

    // Check on mount
    checkInstallReturn();

    // Check on visibility change (when returning from Play Store)
    const handleVisibilityChange = () => {
      checkInstallReturn();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // ✅ CHECK FOR PERSISTENT SESSION (Endless Cookie)
    const isLoggedIn = localStorage.getItem('is_logged_in') === 'true';
    if (isLoggedIn) {
      // Redirect to dashboard if user is already logged in
      window.location.href = '/dashboard';
      return;
    }

    return () => {
      // window.removeEventListener('resize', checkMobile);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);



  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <Helmet>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://koinapp.in/" />
      </Helmet>
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
        isBannerVisible={isBannerVisible}
        transparentMode={true}
        useTextLogo={false}
        simpleNavbar={true}
        sectionLinks={true}
      />
      <PayBanner onBannerStateChange={handleBannerStateChange} />
      <Hero amount={0} />
      <Box sx={{ bgcolor: "background.default", marginTop: 0 }}>

        {/* How KoinApp Works Section */}
        <HowKoinAppWorks />

        {/* Why Choose KoinApp / Benefits */}
        {/* <SecurityBadges /> */}

        {/* Testimonials */}
        {/* <Testimonials /> */}

        {/* FAQ and Footer Section */}
        <Box
          sx={{
            background: "#FFFFFF",
            pt: 0,
            pb: 0,
            mt: 0
          }}
        >
          <FAQ />
          
          {/* CTA Banner */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Container maxWidth="lg" sx={{ px: { xs: 3, md: 2 } }}>
              <Box
                sx={{
                  background: "linear-gradient(120deg, #FFB300 0%, #F57C00 100%)",
                  py: { xs: 4, sm: 6, md: 8 },
                  px: { xs: 3, sm: 4, md: 6 },
                  my: 4,
                  borderRadius: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: { xs: 2, sm: 3, md: 8 },
                  flexDirection: { xs: "column", sm: "row" },
                  textAlign: { xs: "center", sm: "left" },
                  boxShadow: "0 18px 40px rgba(245, 124, 0, 0.25)"
                }}
          >
            {/* Left - Coin Flip GIF */}
            <Box
              sx={{
                flex: { xs: "0 0 auto", sm: "0 0 auto", md: "0 0 auto" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Box
                component="img"
                src={coinFlipGif}
                alt="Coin Flip Animation"
                sx={{
                  width: { xs: "88px", sm: "130px", md: "160px" },
                  height: "auto",
                  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.12))"
                }}
              />
            </Box>

            {/* Right - CTA Text */}
            <Box
              sx={{
                flex: 1,
                textAlign: { xs: "center", sm: "left" }
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.4rem" },
                  fontWeight: 700,
                  color: "#FFFFFF",
                  fontFamily: "General-Sans-bold, sans-serif",
                  mb: 1,
                  lineHeight: 1.2
                }}
              >
                Start earning with KOIN
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", sm: "1.15rem", md: "1.3rem" },
                  fontWeight: 600,
                  color: "rgba(255, 255, 255, 0.92)",
                  fontFamily: "General-Sans-bold, sans-serif",
                  lineHeight: 1.4,
                  mb: 3
                }}
              >
                Download the app and pick your first campaign today.
              </Typography>
              <Button
                href="https://play.google.com/store/apps/details?id=com.koinapp.co&hl=en_IN"
                target="_blank"
                variant="contained"
                endIcon={<DownloadIcon />}
                sx={{
                  background: "#FFFFFF",
                  color: "#E65100",
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                  fontWeight: 700,
                  px: { xs: 3, sm: 4, md: 5 },
                  py: { xs: 1.2, sm: 1.4, md: 1.6 },
                  borderRadius: "999px",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  fontFamily: "General-Sans-bold, sans-serif",
                  boxShadow: "0 6px 16px rgba(143, 50, 0, 0.25)",
                  transition: "all 0.3s ease",
                  "& .MuiSvgIcon-root": {
                    ml: 1,
                    color: "#E65100"
                  },
                  "&:hover": {
                    background: "#FFF8EC",
                    boxShadow: "0 8px 20px rgba(255, 160, 0, 0.4)",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Download KOIN app
              </Button>
            </Box>
              </Box>
            </Container>
          </Box>
          
          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
}